import { memo, startTransition, useEffect, useRef, useState, ComponentProps } from 'react';
import { ProjectCard } from '.';

type ProjectCardProps = ComponentProps<typeof ProjectCard>;

// Defer mounting ProjectCard until it scrolls near the viewport. Cards mount lazily
// and stay mounted thereafter, so scroll position is stable and they don't re-instantiate.
// The intersection callback flips state inside startTransition so mount work is concurrent
// and yields to scroll/input — without it, fast scrolling stalls when many cards cross the
// threshold in one frame and React tries to mount them all synchronously.
const LazyProjectCard_: React.FC<ProjectCardProps> = (props) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (mounted) return;
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            startTransition(() => setMounted(true));
            observer.disconnect();
            return;
          }
        }
      },
      { rootMargin: '400px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [mounted]);

  if (mounted) return <ProjectCard {...props} />;
  // Placeholder matches ProjectCard's outer dimensions so grid layout is stable.
  return <div ref={ref} className="w-full h-56 rounded-xl bg-gray-100 dark:bg-[#242424]" aria-hidden />;
};

export const LazyProjectCard = memo(LazyProjectCard_);
